import { api } from './api';

export interface MKAuthClientData {
  id: string;
  nome: string;
  pessoa: 'fisica' | 'juridica';
  cpf_cnpj: string;
  rg_ie?: string;
  email?: string;
  endereco: string;
  bairro: string;
  cidade: string;
  estado: string;
  cep?: string;
  fone?: string | null;
  celular?: string | null;
  obs?: string | null;
  nascimento?: string | null;
  cadastro?: string;
  login: string;
  senha: string;
  venc: string; // Mudado de number para string
  mac?: string | null;
  ip?: string | null;
  isento: 'nao' | 'sim';
  bloqueado: 'sim' | 'nao';
  plano: string;
  desconto?: string; // Mudado de number para string
  acrescimo?: string; // Mudado de number para string
  endereco_res?: string;
  numero_res?: string;
  bairro_res?: string;
  cidade_res?: string;
  cep_res?: string;
  estado_res?: string;
  complemento_res?: string | null;
  numero?: string;
}

export interface ImportProgress {
  cliente_id?: number;
  nome: string;
  status: 'processando' | 'sucesso' | 'erro';
  erro?: string;
  etapa: 'cliente' | 'contatos' | 'contrato' | 'concluido';
}

export interface ImportSummary {
  total: number;
  success: number;
  errors: number;
  successRate: string;
}

export interface ImportResult {
  summary: ImportSummary;
  results: ImportProgress[];
}

export interface ImportClientsRequest {
  tableData: any[];
  escritorio_id: number;
}

export interface ImportClientsResponse {
  success: boolean;
  data: ImportResult;
  message?: string;
}

export interface ImportAdicionaisRequest {
  tableData: any[];
}

export interface ImportAdicionaisResponse {
  success: boolean;
  data: ImportResult;
  message?: string;
}

export interface ImportGatewaysRequest {
  tableData: any[];
}

export interface ImportGatewaysResponse {
  success: boolean;
  data: ImportResult;
  message?: string;
}

export interface ImportPlansRequest {
  tableData: any[];
}

export interface ImportPlansResponse {
  success: boolean;
  data: ImportResult;
  message?: string;
}

export interface ImportMensalidadesRequest {
  login: string;
  cliente_id: number;
  contrato_id: number;
  url_api: string;
  token_api: string;
}

export interface ImportMensalidadesResponse {
  success: boolean;
  data: {
    contrato_id: number;
    cliente_id: number;
    faturas_cadastradas: number;
    faturas_abertas: number;
    faturas_vencidas: number;
    faturas_pagas: number;
  };
  message?: string;
}

export interface ImportCabosRequest {
  tableData: any[];
}

export interface ImportCabosResponse {
  success: boolean;
  data: ImportResult;
  message?: string;
}

export interface ImportCaixasRequest {
  tableData: any[];
}

export interface ImportCaixasResponse {
  success: boolean;
  data: ImportResult;
  message?: string;
}

export interface ImportCircuitosRequest {
  tableData: any[];
}

export interface ImportCircuitosResponse {
  success: boolean;
  data: ImportResult;
  message?: string;
}

export interface ImportDespesasRequest {
  tableData: any[];
  escritorio_id: number;
}

export interface ImportDespesasResponse {
  success: boolean;
  data: ImportResult;
  message?: string;
}

export interface ImportEquipamentosRequest {
  tableData: any[];
  escritorio_id: number;
}

export interface ImportEquipamentosResponse {
  success: boolean;
  data: ImportResult;
  message?: string;
}

export interface ImportMarcasRequest {
  tableData: any[];
}

export interface ImportMarcasResponse {
  success: boolean;
  data: ImportResult;
  message?: string;
}

export interface ImportModelosRequest {
  tableData: any[];
}

export interface ImportModelosResponse {
  success: boolean;
  data: ImportResult;
  message?: string;
}

export interface ImportOltsRequest {
  tableData: any[];
}

export interface ImportOltsResponse {
  success: boolean;
  data: ImportResult;
  message?: string;
}

class ImportService {
  /**
   * Buscar histórico de importações
   */
  async getImportHistory(): Promise<any> {
    try {
      const response = await api.get('/import/history');
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar histórico');
    }
  }

  /**
   * Importar clientes em lote
   */
  async importClients(data: ImportClientsRequest): Promise<ImportClientsResponse> {
    try {
      const response = await api.post('/import/importClients', {
        tableData: data.tableData,
        escritorio_id: data.escritorio_id
      }) as ImportClientsResponse;
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao importar clientes');
    }
  }

  /**
   * Importar adicionais em lote
   */
  async importAdicionais(data: ImportAdicionaisRequest): Promise<ImportAdicionaisResponse> {
    try {
      const response = await api.post('/import/importAdicionais', {
        tableData: data.tableData
      }) as ImportAdicionaisResponse;
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao importar adicionais');
    }
  }

  /**
   * Importar gateways em lote
   */
  async importGateways(data: ImportGatewaysRequest): Promise<ImportGatewaysResponse> {
    try {
      const response = await api.post('/import/importgateways', {
        tableData: data.tableData
      }) as ImportGatewaysResponse;
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao importar gateways');
    }
  }

  /**
   * Importar planos em lote
   */
  async importPlans(data: ImportPlansRequest): Promise<ImportPlansResponse> {
    try {
      const response = await api.post('/import/importplans', {
        tableData: data.tableData
      }) as ImportPlansResponse;
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao importar planos');
    }
  }

  /**
   * Importar mensalidades de um contrato
   */
  async importMensalidades(data: ImportMensalidadesRequest): Promise<ImportMensalidadesResponse> {
    try {
      const response = await api.post('/import/mensalidades-contrato', {
        login: data.login,
        cliente_id: data.cliente_id,
        contrato_id: data.contrato_id,
        url_api: data.url_api,
        token_api: data.token_api
      }, {
        timeout: 120000 // 2 minutos de timeout
      }) as ImportMensalidadesResponse;
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao importar mensalidades');
    }
  }

  /**
   * Importar cabos em lote
   */
  async importCabos(data: ImportCabosRequest): Promise<ImportCabosResponse> {
    try {
      const response = await api.post('/import/importcabos', {
        tableData: data.tableData
      }) as ImportCabosResponse;
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao importar cabos');
    }
  }

  /**
   * Importar caixas FTTH em lote
   */
  async importCaixas(data: ImportCaixasRequest): Promise<ImportCaixasResponse> {
    try {
      const response = await api.post('/import/importcaixas', {
        tableData: data.tableData
      }) as ImportCaixasResponse;
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao importar caixas FTTH');
    }
  }

  /**
   * Importar circuitos em lote
   */
  async importCircuitos(data: ImportCircuitosRequest): Promise<ImportCircuitosResponse> {
    try {
      const response = await api.post('/import/importcircuitos', {
        tableData: data.tableData
      }) as ImportCircuitosResponse;
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao importar circuitos');
    }
  }

  /**
   * Importar despesas em lote
   */
  async importDespesas(data: ImportDespesasRequest): Promise<ImportDespesasResponse> {
    try {
      const response = await api.post('/import/importdespesas', {
        tableData: data.tableData,
        escritorio_id: data.escritorio_id
      }) as ImportDespesasResponse;
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao importar despesas');
    }
  }

  /**
   * Importar equipamentos em lote
   */
  async importEquipamentos(data: ImportEquipamentosRequest): Promise<ImportEquipamentosResponse> {
    try {
      const response = await api.post('/import/importequipamentos', {
        tableData: data.tableData,
        escritorio_id: data.escritorio_id
      }) as ImportEquipamentosResponse;
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao importar equipamentos');
    }
  }

  /**
   * Importar marcas em lote
   */
  async importMarcas(data: ImportMarcasRequest): Promise<ImportMarcasResponse> {
    try {
      const response = await api.post('/import/importmarcas', {
        tableData: data.tableData
      }) as ImportMarcasResponse;
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao importar marcas');
    }
  }

  /**
   * Importar modelos em lote
   */
  async importModelos(data: ImportModelosRequest): Promise<ImportModelosResponse> {
    try {
      const response = await api.post('/import/importmodelos', {
        tableData: data.tableData
      }) as ImportModelosResponse;
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao importar modelos');
    }
  }

  /**
   * Importar OLTs em lote
   */
  async importOlts(data: ImportOltsRequest): Promise<ImportOltsResponse> {
    try {
      const response = await api.post('/import/importolts', {
        tableData: data.tableData
      }) as ImportOltsResponse;
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao importar OLTs');
    }
  }
}

export const importService = new ImportService();